import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { comPrefixo } from 'volto-ministerio-publico-federal/helpers/Url/subpath';
import React from 'react';
import {
  getImageUrl,
  type NewsCardItem,
} from 'volto-ministerio-publico-federal/components/UI/NewsCardRow';

export interface NewsHighlightCardProps {
  item: NewsCardItem;
  /** `eager` no primeiro destaque: ele abre a página. */
  loading?: 'eager' | 'lazy';
  className?: string;
}

// O link do título cobre o card inteiro pelo `after:inset-0`, sem inchar o
// nome acessível — o anel de foco continua desenhando em volta do texto, que
// é o que o leitor de tela anuncia. `hover:!text-on-primary`: o `a:hover` azul do
// Semantic UI tem mais especificidade que a classe sozinha e repintaria o
// título sobre a foto. Mesma solução do `OVERLAY_LINK` em `DestaqueCard.tsx`.
const HIGHLIGHT_LINK =
  'cursor-pointer rounded text-on-primary after:absolute after:inset-0 hover:!text-on-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/60';

/**
 * Destaque da listagem de notícias: foto sangrada com título e linha de apoio
 * por cima. Proporção 848/535 e raio de 16px vêm do design.
 *
 * `bg-gray-800` embaixo da foto não é enfeite: é o que garante contraste do
 * texto branco enquanto a imagem carrega — ou quando a notícia não tem
 * imagem nenhuma.
 */
export const NewsHighlightCard: React.FC<NewsHighlightCardProps> = ({
  item,
  loading = 'lazy',
  className,
}) => {
  const image = getImageUrl(item);

  return (
    <article
      className={`group relative aspect-[848/535] overflow-hidden rounded-2xl border border-outline bg-gray-800 dark:bg-night-700 ${className ?? ''}`}
    >
      {image && (
        <img
          src={comPrefixo(image)}
          alt=""
          loading={loading}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transform-none motion-reduce:transition-none"
        />
      )}

      <div
        className="mpf-news-highlight__veil pointer-events-none absolute inset-0"
        aria-hidden="true"
      />

      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-6">
        <h3 className="mb-0 text-h2 font-semibold text-on-primary">
          <UniversalLink
            href={flattenToAppURL(item['@id'])}
            className={HIGHLIGHT_LINK}
          >
            {item.title}
          </UniversalLink>
        </h3>

        {item.description && (
          <p className="mb-0 line-clamp-2 text-base font-medium text-on-primary">
            {item.description}
          </p>
        )}
      </div>
    </article>
  );
};

export default NewsHighlightCard;
